import { Injectable } from '@angular/core';
import { Keys,Labels } from '../app/gasflow.constants';
import { UnitsService } from 'pumpcalc-core';
import { Quantity } from 'pumpcalc-core';

export const systemResultMapping = [Keys.gasName, Keys.systemDownstreamPressure];

@Injectable({
  providedIn: 'root'
})
export class ResultsService {

constructor(private unitsService:UnitsService) {}

  readonly systemResultTemplate = {
    [Keys.calculationFailed]:{},
    [Keys.calculationMessage]:{},
    [Keys.gasName]:{},
    [Keys.gasEntryTemperature]:{quantity:Quantity.TEMPERATURE},
    [Keys.systemThroughput]: {quantity:Quantity.THROUGHPUT},
    [Keys.systemThroughputMassFlow] :{quantity:Quantity.THROUGHPUT_MASS_FLOW},
    [Keys.systemThroughputMolarFlow] :{quantity:Quantity.THROUGHPUT_MOLAR_FLOW},
    [Keys.systemConductance] : {quantity:Quantity.SPEED},
    [Keys.systemDownstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.systemDownstreamSpeed]: {quantity:Quantity.SPEED},
    [Keys.systemUpstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.systemUpstreamSpeed]: {quantity:Quantity.SPEED},
  }

  readonly fullResultTemplate = {
    [Keys.calculationFailed]:{},
    [Keys.calculationMessage]:{},
    [Keys.gasName]:{plot:false},
    [Keys.gasEntryTemperature]:{quantity:Quantity.TEMPERATURE},
    [Keys.systemThroughput]: {quantity:Quantity.THROUGHPUT},
    [Keys.systemThroughputMassFlow] :{quantity:Quantity.THROUGHPUT_MASS_FLOW},
    [Keys.systemThroughputMolarFlow] :{quantity:Quantity.THROUGHPUT_MOLAR_FLOW},
    [Keys.systemConductance] : {quantity:Quantity.SPEED},
    [Keys.systemDownstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.systemDownstreamSpeed]: {quantity:Quantity.SPEED},
    [Keys.systemUpstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.systemUpstreamSpeed]: {quantity:Quantity.SPEED},
  }

  readonly simplePipeTemplate = {
    [Keys.crossSection]: {input:true,plot:false},
    [Keys.crossSectionDimension1]: {quantity:Quantity.XDIM,input:true},
    [Keys.crossSectionDimension2]: {quantity:Quantity.XDIM,input:true},
    [Keys.length]:{quantity:Quantity.LENGTH,input:true},
    [Keys.bends]:{input:true},
    [Keys.parallel]:{input:true},
    [Keys.conductance]: {quantity:Quantity.SPEED},
    [Keys.downstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.downstreamSpeed]: {quantity:Quantity.SPEED},
    [Keys.upstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.upstreamSpeed]: {quantity:Quantity.SPEED}
  }

  readonly complexPipeTemplate ={
    [Keys.crossSection]: {input:true,plot:false},
    [Keys.crossSectionDimension1]: {quantity:Quantity.XDIM,input:true},
    [Keys.crossSectionDimension2]: {quantity:Quantity.XDIM,input:true},
    [Keys.length]:{quantity:Quantity.LENGTH,input:true},
    [Keys.bends]:{input:true},
    [Keys.parallel]:{input:true},
    [Keys.molecularTransmissionProbability]:{},
    [Keys.regime]:{},
    [Keys.choking]:{},
    [Keys.chokedPresureRatio]:{},
    [Keys.conductance]: {quantity:Quantity.SPEED},
    [Keys.darcyFrictionFactor]: {},
    [Keys.downstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.downstreamSpeed]: {quantity:Quantity.SPEED},
    [Keys.exitMachNumber]: {},
    [Keys.knudsenNumber]: {},
    [Keys.pressureDifference]: {quantity:Quantity.PRESSURE},
    [Keys.pressureRatio]: {},
    [Keys.regime]: {},
    [Keys.reynoldsNumber]: {},
    [Keys.throughput]: {quantity:Quantity.THROUGHPUT},
    [Keys.upstreamPressure]: {quantity:Quantity.PRESSURE},
    [Keys.upstreamSpeed]: {quantity:Quantity.SPEED}
  }

  public systemResult(solverResult:any,inputConditions:any,id:string,withPipePrefix:boolean):any{
    let systemResult = {};
    systemResult["System"] = this.header();
    Object.keys(this.systemResultTemplate).forEach(key=>{
      systemResult[Labels[key]] = this.value(false,solverResult[key],"",key,Labels[key],null,"System", this.systemResultTemplate[key]?this.systemResultTemplate[key].quantity:null);
    })
    Object.keys(inputConditions).forEach(key=>{
      systemResult[Labels[key]].input=true;
    })
    systemResult[Labels[Keys.gasName]].input=true;
    systemResult[Labels[Keys.gasEntryTemperature]].input=true;
    systemResult[Labels[Keys.gasEntryTemperature]].plot=true;
    solverResult.pipeReports.forEach((pipeReport,index) => {
      systemResult["Pipe " + (index+1)] = this.header();
      this.addPipeFromSolverResult(systemResult, pipeReport, this.simplePipeTemplate,withPipePrefix ? "Pipe " + (index+1) + " " : "",index+1);
    });
    return systemResult;
  }

  public complexResult(solverResult:any,inputConditions:any,id:string, withPipePrefix:boolean):any{
    let complexResult = {};
    complexResult["System"] = this.header();
    Object.keys(this.systemResultTemplate).forEach(key=>{
      complexResult[Labels[Keys[key]]] = this.value(false,solverResult[key],"",key,Labels[key],null,"System",this.systemResultTemplate[key]?this.systemResultTemplate[key].quantity:null);
    })
    Object.keys(inputConditions).forEach(key=>{
      complexResult[Labels[key]].input=true;
    })
    complexResult[Labels[Keys.gasName]].input=true;
    complexResult[Labels[Keys.gasEntryTemperature]].input=true;
    complexResult[Labels[Keys.gasEntryTemperature]].plot=true;
    solverResult.pipeReports.forEach((pipeReport,index) => {
      complexResult["Pipe " + (index+1)] = this.header();
      this.addPipeFromSolverResult(complexResult, pipeReport, this.complexPipeTemplate,withPipePrefix ? "Pipe " + (index+1) + " " : "",index+1);
    });
    return complexResult;
  }

  addPipeFromSolverResult(toResult:any, fromPipeReport:any, usingTemplate:any,labelPrefix:any,pipeIndex:number){
    Object.keys(usingTemplate).forEach(key => {
      toResult[labelPrefix + Labels[key]] = this.value(usingTemplate[key].input?usingTemplate[key].input:false,fromPipeReport[key],labelPrefix,key,Labels[key],pipeIndex,"Pipe " + pipeIndex, usingTemplate[key].quantity);
    })
    return toResult;
  }

  value(input,value,labelPrefix:string,key:string,label:string,pipeIndex:number,heading:string,quantity?:string){
    return {input:input,value:value,quantity:quantity?quantity:undefined,labelPrefix:labelPrefix,key:key,label:label,pipeIndex:pipeIndex,heading:heading};
  }

  header(){
    return {isHeading:true,value:""};
  }

}
